import React from 'react';
import { PageSEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Factory, Target, CheckCircle, Users, Award, Star, Rocket, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const QRMSoftwarePartners = () => {
  const tacticalPartners = [
    {
      id: 1,
      name: "CIM (Computer Integrated Manufacturing)",
      category: "ERP Systems",
      description: "Vandenbussche CAD • CAM • ERP - Computer Integrated Manufacturing solution supporting digital transformation and manufacturing integration.",
      website: "https://www.vandenbusschenv.be/en",
      logoUrl: "https://customer-assets.emergentagent.com/job_qrm-navigator/artifacts/4rgvhbuu_image.png",
      specialization: "CAD, CAM, ERP Integration, Manufacturing Software"
    },
    {
      id: 2,
      name: "PROPOS PLANNING",
      category: "Production Planning",
      description: "Advanced production planning software with 'optimal flow' philosophy. Visualizes company backlog, task prioritization, and workload like an airport information board.",
      website: "https://www.propos-software.com/",
      logoUrl: "https://customer-assets.emergentagent.com/job_qrm-navigator/artifacts/rg3ez1tw_image.png",
      specialization: "Production Planning, Visual Management, Optimal Flow"
    },
    {
      id: 3,
      name: "AZUMUTA",
      category: "Digital Work Instructions",
      description: "AI-driven Connected Worker Platform that eliminates paperwork on the factory floor. Provides digital work instructions, quality assurance, skill matrix & training, and digital checklists with real-time data integration.",
      website: "https://www.azumuta.com/",
      logoUrl: "https://customer-assets.emergentagent.com/job_qrm-navigator/artifacts/5spdme18_image.png",
      specialization: "AI-Powered Digital Instructions, Quality Management, Connected Worker Platform, Shop Floor Digitization"
    },
    {
      id: 4,
      name: "TRUMPF OSEON",
      category: "Manufacturing Technology",
      description: "Comprehensive production control solution for sheet metal processing. Modular software (Go→Grow→Flow) that digitalizes core processes, provides transparent material flow control, and creates seamless information flow across the entire production chain.",
      website: "https://www.trumpf.com/en_INT/products/software/oseon/",
      logoUrl: "https://customer-assets.emergentagent.com/job_qrm-navigator/artifacts/bdui08qa_image.png",
      specialization: "Sheet Metal Production Control, Material Flow Management, Digital Manufacturing, Process Integration"
    },
    {
      id: 5,
      name: "QUOTATION FACTORY",
      category: "Sales & Quoting",
      description: "Advanced metalworking quotation software that enables accurate quotes in minutes without tying up technical personnel. Delivers up to 80% of quotes with self-service portal and automatic estimations.",
      website: "https://quotationfactory.com/en/",
      logoUrl: "https://customer-assets.emergentagent.com/job_qrm-navigator/artifacts/804ohrul_image.png",
      specialization: "Metalworking Quotation Software, Sales Automation, Customer Self-Service"
    },
    {
      id: 6,
      name: "SHEET METAL CONNECT",
      category: "Digital Transformation",
      description: "Independent digitalization consulting for sheet metal fabricators. Guides companies through Industry 4.0 transformation with unified namespace architecture and real-time production visibility.",
      website: "https://www.sheetmetalconnect.com/",
      logoUrl: "https://customer-assets.emergentagent.com/job_qrm-navigator/artifacts/g0bkoawp_image.png",
      specialization: "Digital Transformation, Industry 4.0, Systems Integration, Manufacturing Consulting"
    },
  ];

  const strategicPartner = {
    id: 7,
    name: "4RESULTS",
    category: "Leadership Training (VR Simulation)",
    description: "We transform followers into leaders through a unique leadership training combining David Marquet's Leader-to-Leader approach with a virtual reality simulation. Our method combines know-how, practice, and debriefs to establish a culture of psychological safety, intent-based leadership, and profound accountability. Applied broadly, this comprehensive approach serves as a powerful instrument for entire organizational transformation. Participants not only grasp contemporary leadership concepts but train them thoroughly for immediate application in their roles. Our 8-module process supports change management, individual development, and operational excellence.",
    website: "https://4results.team/eng/",
    logoUrl: "/4results-logo.png",
    specialization: "Intent-Based Leadership, Psychological Safety, Virtual Reality Simulation, Organizational Transformation, Change Management"
  };

  const handlePartnerClick = async (partner) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track-partner-visit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          partner_name: partner.name,
          partner_website: partner.website,
          timestamp: new Date().toISOString(),
          visitor_info: { user_agent: navigator.userAgent, referrer: window.location.href }
        })
      });
    } catch (error) {
      console.error('Error tracking partner visit:', error);
    }
    window.open(partner.website, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-12">
      <PageSEO path="/software-partners" />
      <div className="container mx-auto px-6">
        <div className="max-w-7xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">QRM Partnership Ecosystem</h1>
            <p className="text-xl text-gray-600 mb-2">
              7 Proven Partners for Quick Responsive Manufacturing Transformation
            </p>
            <p className="text-lg text-gray-500">
              Tactical software solutions and strategic leadership partners — endorsed by Jan Pauwels
            </p>
          </div>

          {/* Partner Showcase Info */}
          <Card className="p-8 mb-12 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Award className="mr-3 h-6 w-6 text-blue-600" />
              Partner Showcase Program
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Why These Partners</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="mr-2 h-5 w-5 text-green-500 mt-0.5 shrink-0" />
                    <span><strong>Proven Results:</strong> Each partner contributed to successful QRM transformations</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="mr-2 h-5 w-5 text-green-500 mt-0.5 shrink-0" />
                    <span><strong>Real Implementation:</strong> Used in actual 18-software MES integration</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="mr-2 h-5 w-5 text-green-500 mt-0.5 shrink-0" />
                    <span><strong>QRM-Compatible:</strong> Solutions that support QRM principles and implementation</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="mr-2 h-5 w-5 text-green-500 mt-0.5 shrink-0" />
                    <span><strong>Jan Pauwels Endorsed:</strong> Personally selected and validated by QRM expertise</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-6 p-4 bg-white rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Partner Program:</strong> Exclusive showcase of software solutions that support QRM transformation success. 
                <a href="mailto:info@smartqrmnavigator.com" className="underline font-medium"> Contact info@smartqrmnavigator.com</a> for partnership opportunities.
              </p>
            </div>
          </Card>

          {/* 18-Software MES Integration */}
          <Card className="p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">18-Software MES Integration</h2>
            <p className="text-gray-700 mb-6 leading-relaxed">
              QRM success is enabled by seamless integration of multiple software packages working fluently together. 
              This digital transformation approach is a key factor in achieving significant lead time reduction and improved EBITDA margins.
            </p>
            <div className="grid grid-cols-3 md:grid-cols-7 gap-3">
              {['ERP', 'Planning', 'Instructions', 'Technology', 'Sales', 'Digital', 'Leadership'].map((cat, index) => (
                <div key={index} className="text-center p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm font-medium text-gray-700">{cat}</div>
                </div>
              ))}
            </div>
          </Card>

          {/* ═══ STRATEGIC PARTNER — 4RESULTS ═══ */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Star className="h-5 w-5 text-yellow-500" />
              Strategic Partner
            </h2>
            <Card className="p-6 border-2 border-yellow-300 bg-gradient-to-r from-yellow-50/50 to-white hover:shadow-xl transition-all duration-300" data-testid="strategic-partner-card">
              <div className="flex flex-col lg:flex-row gap-6">
                {/* Left: Logo & Badge */}
                <div className="lg:w-64 shrink-0">
                  <div className="flex items-center gap-2 mb-3">
                    <Star className="h-4 w-4 text-yellow-500" />
                    <Badge className="bg-yellow-100 text-yellow-800 text-xs font-bold">
                      STRATEGIC QRM PARTNER
                    </Badge>
                  </div>
                  <div className="w-full h-24 bg-gray-900 rounded-lg flex items-center justify-center overflow-hidden border border-gray-200">
                    <img 
                      src={strategicPartner.logoUrl}
                      alt="4RESULTS — Strategic QRM Partner endorsed by Jan Pauwels"
                      className="max-h-20 max-w-full object-contain p-3"
                    />
                  </div>
                </div>
                {/* Right: Content */}
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">{strategicPartner.name}</h3>
                  <p className="text-sm text-blue-600 font-semibold mb-3">{strategicPartner.category}</p>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">{strategicPartner.description}</p>
                  <div className="mb-4">
                    <h4 className="font-medium text-gray-700 text-sm mb-1">Specialization:</h4>
                    <p className="text-gray-600 text-sm">{strategicPartner.specialization}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <Button 
                      className="bg-blue-600 hover:bg-blue-700 text-white font-bold"
                      onClick={() => handlePartnerClick(strategicPartner)}
                      data-testid="4results-visit-btn"
                    >
                      <ArrowRight className="mr-2 h-4 w-4" />
                      Visit 4RESULTS
                    </Button>
                    <p className="text-xs text-gray-500">Clicks tracked for partnership analytics</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* ═══ TACTICAL PARTNERS ═══ */}
          <div className="mb-12">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Rocket className="h-5 w-5 text-blue-500" />
              Tactical Partners
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {tacticalPartners.map((partner) => (
                <Card key={partner.id} className="p-6 hover:shadow-xl transition-all duration-300 flex flex-col" data-testid={`partner-card-${partner.id}`}>
                  <div className="flex items-center gap-2 mb-4">
                    <Rocket className="h-4 w-4 text-blue-500" />
                    <Badge className="bg-blue-100 text-blue-800 text-xs">
                      QRM PARTNER
                    </Badge>
                  </div>

                  {/* Partner Logo */}
                  <div className="w-full h-20 bg-gray-100 rounded-lg flex items-center justify-center mb-4 border overflow-hidden">
                    <img 
                      src={partner.logoUrl}
                      alt={`${partner.name} — QRM software partner endorsed by Jan Pauwels`}
                      className="max-h-16 max-w-full object-contain"
                    />
                  </div>

                  <h3 className="text-lg font-bold text-gray-900 mb-2">{partner.name}</h3>
                  <p className="text-sm text-blue-600 font-medium mb-3">{partner.category}</p>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{partner.description}</p>

                  {/* Specialization */}
                  <div className="mb-4">
                    <h4 className="font-medium text-gray-700 text-sm mb-1">Specialization:</h4>
                    <p className="text-gray-600 text-sm">{partner.specialization}</p>
                  </div>

                  {/* Visit Button */}
                  <Button 
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold"
                    onClick={() => handlePartnerClick(partner)}
                  >
                    <ArrowRight className="mr-2 h-4 w-4" />
                    Visit {partner.name.split(' ')[0]}
                  </Button>
                  <p className="text-xs text-gray-500 mt-2 text-center">Clicks tracked for partnership analytics</p>
                </Card>
              ))}
            </div>
          </div>

          {/* Partnership Value */}
          <Card className="p-8 mb-12 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Factory className="mr-3 h-6 w-6 text-green-600" />
              Digital Transformation Success
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Proven Software Integration</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start"><CheckCircle className="mr-2 h-5 w-5 text-green-500 mt-0.5 shrink-0" /><span><strong>18 Software Packages:</strong> Seamlessly integrated for QRM success</span></li>
                  <li className="flex items-start"><CheckCircle className="mr-2 h-5 w-5 text-green-500 mt-0.5 shrink-0" /><span><strong>Digital Transformation:</strong> Key enabler of significant lead time reduction</span></li>
                  <li className="flex items-start"><CheckCircle className="mr-2 h-5 w-5 text-green-500 mt-0.5 shrink-0" /><span><strong>Real-World Validation:</strong> Each solution tested in actual implementation</span></li>
                  <li className="flex items-start"><CheckCircle className="mr-2 h-5 w-5 text-green-500 mt-0.5 shrink-0" /><span><strong>Industry Recognition:</strong> Recognized for best QRM practice</span></li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">QRM-Compatible Solutions</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start"><Target className="mr-2 h-5 w-5 text-blue-500 mt-0.5 shrink-0" /><span><strong>Time-Focused:</strong> Support QRM's emphasis on lead time reduction</span></li>
                  <li className="flex items-start"><Target className="mr-2 h-5 w-5 text-blue-500 mt-0.5 shrink-0" /><span><strong>Flow Optimization:</strong> Enable smooth material and information flow</span></li>
                  <li className="flex items-start"><Target className="mr-2 h-5 w-5 text-blue-500 mt-0.5 shrink-0" /><span><strong>Visual Management:</strong> Provide transparency and quick decision-making</span></li>
                  <li className="flex items-start"><Target className="mr-2 h-5 w-5 text-blue-500 mt-0.5 shrink-0" /><span><strong>Integration Ready:</strong> Work together as unified MES ecosystem</span></li>
                </ul>
              </div>
            </div>
          </Card>

          {/* Interested in Partnering */}
          <Card className="p-8 bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Interested in Becoming a QRM Partner?</h2>
            <p className="text-gray-600 mb-6">
              Join our exclusive partner showcase and reach manufacturing executives implementing QRM transformation
            </p>
            <Button 
              size="lg" 
              className="px-8 py-3 bg-yellow-500 hover:bg-yellow-400 text-blue-900 font-semibold"
              onClick={() => window.open('mailto:info@smartqrmnavigator.com?subject=QRM Partnership Interest', '_blank')}
            >
              <Users className="mr-2 h-5 w-5" />
              Contact for Partnership: info@smartqrmnavigator.com
            </Button>
          </Card>

          {/* Navigation */}
          <div className="text-center mt-12">
            <div className="space-x-4">
              <Button variant="outline" onClick={() => window.location.href = '/success-cases'} className="px-8 py-3">
                Success Cases
              </Button>
              <Button onClick={() => window.location.href = '/'} className="px-8 py-3 bg-blue-600 hover:bg-blue-700">
                Back to Home
              </Button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default QRMSoftwarePartners;
